import { FC, ReactNode } from 'react';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { PaginatedTable } from 'components/table/paginated-table';
import { FloatingTooltip } from 'components/floating-tooltip';
import { CitationResponse } from 'types/api-responses';

import style from './citation-match-table.module.scss';

interface CitationMatchTableProps {
  citations: CitationResponse[];
  extractedColumnHeader: string;
  extractedColumnTooltip?: string;
  matchColumnHeader: string;
  renderExtracted?: (citation: CitationResponse) => ReactNode;
  renderMatch: (citation: CitationResponse) => ReactNode;
  testId?: string;
}

const CitationMatchTable: FC<CitationMatchTableProps> = ({
  citations,
  extractedColumnHeader,
  extractedColumnTooltip,
  matchColumnHeader,
  renderExtracted,
  renderMatch,
  testId,
}) => {
  if (citations.length === 0) {
    return null;
  }

  return (
    <div className={style.sectionSpacing}>
      <PaginatedTable
        pageSizes={[10, 20, 30]}
        pageSize={10}
        page={1}
        totalItems={citations.length}
        onPaginate={() => {}}
        isLoading={false}
        emptyStateComponent={null}
        data-testid={testId}
      >
        <TableHead>
          <TableRow>
            <TableHeader>
              <span className={style.headerWithTooltip}>
                {extractedColumnHeader}
                {extractedColumnTooltip && (
                  <FloatingTooltip
                    content={extractedColumnTooltip}
                    placement="top"
                  >
                    <Information />
                  </FloatingTooltip>
                )}
              </span>
            </TableHeader>
            <TableHeader className={style.matchHeader}>
              {matchColumnHeader}
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {citations.map((citation) => (
            <TableRow key={citation.id}>
              <TableCell>
                {renderExtracted
                  ? renderExtracted(citation)
                  : citation.provisionValue}
              </TableCell>
              <TableCell>{renderMatch(citation)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </PaginatedTable>
    </div>
  );
};

export { CitationMatchTable };
export type { CitationMatchTableProps };
